import cx from 'classnames';
import { useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { PDFDownloadLink } from '@react-pdf/renderer';

import CaretDownIcon from 'components/icons/CaretDownIcon';
import DropArrowMiddleIcon from 'components/icons/DropArrowMiddleIcon';
import CertificatePDF from 'components/certificates-table/certificate-pdf';
import { Button } from 'components/common/button/button';
import { TableNestedHead, TableNestedRow } from './grid-table-nested-elements';
import { useNormalizeCell } from './use-normalize-cell';
import { DELEGATE_PAGE } from 'site-settings/site-navigation';
import { formatTokenAmount } from 'utils/amounts';

import { SortableProp, ColumnItemProp } from 'types';

import {
  StyledTableCell,
  StyledTableHead,
  StyledTableRow,
  StyledTableLine,
  StyledTableButton,
  StyledTableNested
} from './grid-table.style';

type TableHeadProp = {
  cols: number;
  columns: ColumnItemProp[];
  requestSort: (dataIndex: string) => void;
  sortConfig: SortableProp | null;
  variant?: string;
};

export const TableHead = ({
  cols = 5,
  variant,
  columns,
  requestSort,
  sortConfig
}: TableHeadProp) => {
  const getClassNamesFor = (name: string) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : '';
  };

  return (
    <StyledTableHead className={`variant-${variant}`}>
      <StyledTableRow className={`variant-${variant}`}>
        <StyledTableLine columns={cols} className={`variant-${variant}`}>
          {columns.map(({ title, dataIndex, sortable }) => (
            <StyledTableCell
              key={dataIndex}
              className="table-column-has-sorters"
            >
              <div
                className={cx(
                  'table-column-sorters',
                  getClassNamesFor(dataIndex),
                  {
                    'has-sortable': sortable
                  }
                )}
                role="presentation"
                onClick={sortable ? () => requestSort(dataIndex) : undefined}
              >
                <span className="table-column-title">{title}</span>
                {sortable && (
                  <span className="table-column-sorter">
                    <span className="table-caret">
                      <CaretDownIcon />
                    </span>
                  </span>
                )}
              </div>
            </StyledTableCell>
          ))}
        </StyledTableLine>
      </StyledTableRow>
    </StyledTableHead>
  );
};

type TableCellProp<T> = {
  item: T;
  dataIndex: string;
};

export const TableCell = <T,>({ item, dataIndex }: TableCellProp<T>) => {
  const { cellValue, className } = useNormalizeCell(item, dataIndex);
  return <StyledTableCell className={className}>{cellValue}</StyledTableCell>;
};

type TableRowProp<T> = {
  columns: ColumnItemProp[];
  item: T[];
  isLast: boolean,
  accountId?: string;
  last?: boolean;
  variant?: string;
  hover?: boolean;
};

export const TableRow = forwardRef<HTMLDivElement, any>(
  <T,>(
    { columns, hover, variant, accountId, item, isLast }: TableRowProp<T>,
    ref
  ) => {
    const [show, setToggle] = useState(false);
    const navigate = useNavigate();

    const result: Record<string, any> = item;

    const onClickHandler = (item) => {
      navigate(DELEGATE_PAGE + '/' + encodeURIComponent(item?.validator));
    };

    return (
      <StyledTableRow
        className={cx(`variant-${variant}`)}
        {...(isLast ? { ref } : {})}
      >
        <StyledTableLine
          className={cx(`variant-${variant}`, {
            'is--hover': hover
          })}
          onClick={hover ? () => onClickHandler(result) : undefined}
          columns={columns.length}
        >
          {columns.map(({ dataIndex }) => (
            <TableCell key={dataIndex} item={result} dataIndex={dataIndex} />
          ))}
          {variant === 'certificate' && (
            <>
              <StyledTableCell>
                <PDFDownloadLink
                  document={
                    <CertificatePDF
                      data={result.projects}
                      title={result.title}
                      accountId={accountId}
                      amount={formatTokenAmount(result.offset_amount, 24, 5)}
                    />
                  }
                  fileName={`certificate-${result?.token_id}.pdf`}
                >
                  {() => (
                    <Button fullwidth size="middle" variant="outlined">
                      Download
                    </Button>
                  )}
                </PDFDownloadLink>
              </StyledTableCell>

              <StyledTableButton
                className={cx({
                  active: show
                })}
                onClick={() => setToggle(!show)}
              >
                <DropArrowMiddleIcon />
              </StyledTableButton>
            </>
          )}
        </StyledTableLine>
        <AnimatePresence initial={false}>
          {show && (
            <motion.div
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 }
              }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            >
              <StyledTableNested>
                <TableNestedHead />
                {result.projects.map((item) => (
                  <TableNestedRow key={item.project_id} item={item} />
                ))}
              </StyledTableNested>
            </motion.div>
          )}
        </AnimatePresence>
      </StyledTableRow>
    );
  }
);
